import { FC, Suspense } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'
import IEBanner from 'pages/InternetExplorer/IEBanner'
import ProtectedRoute from 'ui/atoms/ProtectedRoute'
import routePaths from 'const/routePaths'
import Login from 'pages/Login'
import SessionExpired from 'ui/organisms/SessionTimeOut/SessionExpired'
import { clientInfo } from 'configureBrand'
import { PageLoader, PublicRoute } from './subcomponents'
import {
  AccountSetupConfirmationLazy,
  AccountSummaryLazy,
  BatchEditJobNamesLazy,
  CliAutomationLazy,
  CloseAccountLazy,
  CreditLazy,
  EditBusinessInformationLazy,
  EditUserInformationLazy,
  EmailStatementLazy,
  FirstTimeLoginLazy,
  InvoiceBillPayLazy,
  InvoiceLazy,
  LogoutLazy,
  LostCardConfirmationLazy,
  LostCardLazy,
  ManageBuyersLazy,
  ManageChildAccountLazy,
  ManageLinkedAccountsLazy,
  ManagePaymentAccountLazy,
  ManageUserLazy,
  PageNotFoundLazy,
  PaymentsLazy,
  RecoverUserIDLazy,
  RegisterChildAccountLazy,
  RegistrationLazy,
  RequestAdditionalBuyersLazy,
  ResetPasswordLazy,
  SecureMessageLazy,
  StatementLazy,
  SystemErrorLazy,
  TaxFormsLazy,
  TransactionsLazy,
  UserGuideLazy,
  UserGuideVideosLazy,
  VerifyIbpStatusLazy,
  ViewPaymentLazy
} from './lazyLoad'

const Routes: FC<{ isIE?: boolean }> = ({ isIE }) => {
  if (isIE) {
    return (
      <Switch>
        <PublicRoute exact path={routePaths.home} component={IEBanner} />
        <Redirect to={routePaths.home} />
      </Switch>
    )
  }

  /**
   * Handles routes specific to the JCPenney Use case where the only feature that
   * should be available is the Workstation CLI Questionnaire feature. Login page
   * and the rest of the app is not available for the JCPenney Client.
   */
  if (clientInfo.clientName === 'jcpenney') {
    return (
      <Suspense fallback={<PageLoader />}>
        <PageNotFoundLazy />
      </Suspense>
    )
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <Switch>
        <PublicRoute exact path={routePaths.home} component={Login} />
        <ProtectedRoute
          exact
          path={routePaths.firstTimeLogin}
          component={FirstTimeLoginLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.firstTimeLoginConfirmation}
          component={AccountSetupConfirmationLazy}
        />
        <PublicRoute exact path={routePaths.logout} component={LogoutLazy} />
        <PublicRoute
          exact
          path={routePaths.recoverUserId}
          component={RecoverUserIDLazy}
        />
        <PublicRoute
          exact
          path={routePaths.resetPassword}
          component={ResetPasswordLazy}
        />
        <PublicRoute
          exact
          path={routePaths.registration}
          component={RegistrationLazy}
        />
        <PublicRoute
          exact
          path={routePaths.registerChildAccount}
          component={RegisterChildAccountLazy}
        />
        <PublicRoute
          exact
          path={routePaths.lostCardConfirmation}
          component={LostCardConfirmationLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.updatePassword}
          component={ResetPasswordLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.summary}
          component={AccountSummaryLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.makePayment}
          component={PaymentsLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.viewPayment}
          component={ViewPaymentLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.invoice}
          component={InvoiceLazy}
        />
        <ProtectedRoute exact path={routePaths.credit} component={CreditLazy} />
        <ProtectedRoute
          exact
          path={routePaths.editBusinessInfo}
          component={EditBusinessInformationLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.manageBuyers}
          component={ManageBuyersLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.managePayment}
          component={ManagePaymentAccountLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.increaseCreditLine}
          component={CliAutomationLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.taxForms}
          component={TaxFormsLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.userGuide}
          component={UserGuideLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.closeAccount}
          component={CloseAccountLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.editJobName}
          component={BatchEditJobNamesLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.statement}
          component={StatementLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.editUser}
          component={EditUserInformationLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.emailStatement}
          component={EmailStatementLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.manageUser}
          component={ManageUserLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.manageLinkedAccounts}
          component={ManageLinkedAccountsLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.invoiceBillPay}
          component={InvoiceBillPayLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.ibpStatus}
          component={VerifyIbpStatusLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.contactUs}
          component={SecureMessageLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.manageChildAccount}
          component={ManageChildAccountLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.videoGallery}
          component={UserGuideVideosLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.transactions}
          component={TransactionsLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.requestBuyerCards}
          component={RequestAdditionalBuyersLazy}
        />
        <ProtectedRoute
          exact
          path={routePaths.lostCard}
          component={LostCardLazy}
        />

        {/**
         * Removed the Lazy load for the Session Expired Component
         * because this component is used in the SessionTimeOut Modal(which is imported at the App.tsx).
         * Thus lazy loading the Session Expired Component doesn't have any benefit.
         */}
        <Route
          exact
          path={routePaths.sessionExpired}
          component={SessionExpired}
        />
        <Route exact path={routePaths.error} component={SystemErrorLazy} />
        <Route component={PageNotFoundLazy} />
      </Switch>
    </Suspense>
  )
}

export default Routes
